// Contexto de Autenticación
// Los tokens JWT viajan en cookies httpOnly — no se leen desde JavaScript.
// La verificación de sesión se hace llamando a /api/auth/me con la cookie.
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '@/services/authService';
import type { LoginRequest, AuthState } from '@/types';

interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<{ requires_2fa?: boolean; pre_auth_token?: string } | void>;
  completeLogin: (data: { user: any; tokens: any; permissions: any }) => Promise<void>;
  logout: (reason?: 'manual' | 'inactivity') => Promise<void>;
  refreshUser: () => Promise<void>;
  hasPermission: (module: string, action: string) => boolean;
  hasAnyPermission: (checks: Array<{ module: string; action: string }>) => boolean;
  hasAllPermissions: (checks: Array<{ module: string; action: string }>) => boolean;
  token: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    tokens: {
      accessToken: null,
      refreshToken: null,
    },
    permissions: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Verificar sesión al arrancar — la cookie httpOnly se envía automáticamente
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // /me usa la cookie access_token enviada automáticamente (withCredentials: true)
        const user = await authService.getMe();
        const permissions = authService.getStoredPermissions();

        setState({
          user,
          tokens: { accessToken: null, refreshToken: null }, // tokens en cookie, no en memoria
          permissions,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch {
        // Sin sesión válida (401 o cookie expirada)
        authService.clearStorage();
        setState({
          user: null,
          tokens: { accessToken: null, refreshToken: null },
          permissions: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    };

    initializeAuth();
  }, []);

  /**
   * Iniciar sesión. Si el usuario tiene 2FA activo, retorna { requires_2fa, pre_auth_token }
   * sin modificar el estado de autenticación.
   */
  const login = async (credentials: LoginRequest) => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));

      const response = await authService.login(credentials);
      const data: any = response.data || response;

      // Caso 2FA requerido
      if (data.requires_2fa && data.pre_auth_token) {
        setState((prev) => ({ ...prev, isLoading: false }));
        return { requires_2fa: true as const, pre_auth_token: data.pre_auth_token as string };
      }

      if (response.success && data.user && data.permissions) {
        await completeLogin(data);
      }
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  /**
   * Finaliza el proceso de login (usado tanto en login normal como tras verificar 2FA).
   * Los tokens ya están en cookies httpOnly — solo guardamos datos de sesión no-sensibles.
   */
  const completeLogin = async (data: { user: any; tokens: any; permissions: any }) => {
    const { permissions } = data;

    // Guardar solo datos no-sensibles en localStorage
    localStorage.setItem('permissions', JSON.stringify(permissions));

    // Obtener datos frescos del servidor (foto, avatar_thumbnail, etc.)
    const user = await authService.getMe();

    setState({
      user,
      tokens: { accessToken: null, refreshToken: null }, // tokens en cookie
      permissions,
      isAuthenticated: true,
      isLoading: false,
    });

    window.dispatchEvent(new CustomEvent('auth-login', { detail: {} }));
  };

  /**
   * Cerrar sesión
   */
  const logout = async (reason: 'manual' | 'inactivity' = 'manual') => {
    try {
      await authService.logout(reason);
    } finally {
      setState({
        user: null,
        tokens: { accessToken: null, refreshToken: null },
        permissions: null,
        isAuthenticated: false,
        isLoading: false,
      });

      window.dispatchEvent(new Event('auth-logout'));
    }
  };

  /**
   * Refrescar datos del usuario
   */
  const refreshUser = async () => {
    try {
      const user = await authService.getMe();
      setState((prev) => ({ ...prev, user }));
    } catch (error) {
      console.error('Error al refrescar usuario:', error);
    }
  };

  const hasPermission = (module: string, action: string): boolean => {
    return state.permissions?.[module]?.[action] === true;
  };

  const hasAnyPermission = (checks: Array<{ module: string; action: string }>): boolean => {
    return checks.some(({ module, action }) => hasPermission(module, action));
  };

  const hasAllPermissions = (checks: Array<{ module: string; action: string }>): boolean => {
    return checks.every(({ module, action }) => hasPermission(module, action));
  };

  const value: AuthContextType = {
    ...state,
    login,
    completeLogin,
    logout,
    refreshUser,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    token: null, // tokens en cookie httpOnly — no accesible desde JS
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook para usar el contexto de autenticación
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export default AuthContext;
